<!--
  Patensys v2:
  This component is used to add a password to a user account.  It is used when a user is created without a password (i.e., 
  via email signin link).
  The user can add a password so that it can signin with email/password next time.
-->
<template>
  <!-- Change password POPUP -->
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="cancel">
      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="min-h-screen px-4 text-center">
          <TransitionChild
            as="changepassword"
            enter="duration-300 ease-out"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <DialogOverlay class="fixed inset-0" />
          </TransitionChild>

          <span
            class="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
            >&#8203;</span
          >

          <TransitionChild
            as="profile"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <div
              class="
                inline-block
                w-full
                max-w-md
                p-6
                my-8
                overflow-hidden
                text-left
                align-middle
                transition-all
                transform
                bg-white
                shadow-xl
                rounded-2xl
                border border-gray-300
              "
            >
              <DialogTitle
                as="h3"
                class="text-lg font-medium leading-6 text-gray-900 text-center"
              >
                Add Password
              </DialogTitle>
              <div>
                <div v-if="error" class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                  <ErrorAlert v-on:close="closeError" v-bind:error="error"></ErrorAlert>
                </div>
                <div v-if="info" class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                  <InfoAlert v-on:close="closeInfo" v-bind:info="info"></InfoAlert>
                </div>
                <div class="my-3">
                  <div class="w-1/3 inline-block">Password:</div>
                  <div class="w-1/2 inline-block">
                    <input
                      type="password"
                      :size="18"
                      v-model="newpassword"
                      class="
                      block
                      w-full
                      shadow-sm
                      text-md
                      focus:ring-indigo-500
                      focus:border-indigo-500
                      border-gray-300
                      -ml-3
                      rounded-md
                      "
                      @input="checkPassword"
                    />
                  </div>
                </div>
              </div>

              <div class="">
                <div class="w-1/3 inline-block">Confirm:</div>
                  <div class="w-1/2 inline-block">
                    <input
                      type="password"
                      :size="18"
                      v-model="confirmPassword"
                      class="
                      block
                      w-full
                      shadow-sm
                      text-md
                      focus:ring-indigo-500
                      focus:border-indigo-500
                      border-gray-300
                      -ml-3
                      rounded-md
                      "
                      @input="checkPassword"
                    />
                  </div>
              </div>

              <div class="text-left text-sm" v-show="show">
                <div class="font-bold">
                  <h1>Password Requirements</h1>
                </div>

                <div class="input_container text-red-500">
                  <ul>
                    <li
                      v-bind:class="{
                        'line-through text-gray-400': containsEightCharacters,
                      }"
                    >
                      8 Characters
                    </li>
                    <li
                      v-bind:class="{
                        'line-through text-gray-400': containsNumber,
                      }"
                    >
                      Contains Number
                    </li>
                    <li
                      v-bind:class="{
                        'line-through text-gray-400': containsUppercase,
                      }"
                    >
                      Contains Uppercase
                    </li>
                    <li
                      v-bind:class="{
                        'line-through text-gray-400': containsSpecialCharacter,
                      }"
                    >
                      Contains Special Character
                    </li>
                    <li
                      v-bind:class="{
                        'line-through text-gray-400': match,
                      }"
                    >
                      Matching Password
                    </li>
                  </ul>
                </div>
              </div>

              <div class="mt-4">
                <span class="space-x-2">
                  <button
                    type="button"
                    class="
                      inline-flex
                      justify-center
                      px-4
                      py-2
                      text-sm
                      font-medium
                      border border-transparent
                      rounded-md
                      focus:outline-none
                      focus-visible:ring-2
                      focus-visible:ring-offset-2
                      focus-visible:ring-purple-500
                    "
                    :class="{
                      'text-purple-600 bg-purple-100 hover:bg-purple-200': validPassword,
                      'text-gray-400 bg-gray-200 cursor-not-allowed opacity-50': !validPassword
                    }"
                    :disabled="!validPassword"
                    @click="savePassword"
                  >
                    Save Password
                  </button>

                  <button
                    type="button"
                    class="
                      inline-flex
                      justify-center
                      px-4
                      py-2
                      text-sm
                      font-medium
                      text-red-400
                      border border-transparent
                      rounded-md
                      hover:text-red-600
                      focus:outline-none
                      focus-visible:ring-2
                      focus-visible:ring-offset-2
                      focus-visible:ring-blue-500
                    "
                    @click="cancel"
                  >
                    Cancel
                  </button>
                </span>
              </div>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogOverlay,
  DialogTitle,
} from "@headlessui/vue";
import { addPassword, updateUser } from "@/services/user.js";
import ErrorAlert from "@/components/alerts/ErrorAlert.vue";
import InfoAlert from "@/components/alerts/InfoAlert.vue";

export default {
  name: "AddPassword",
  props: ["isOpen"],
  components: {
    Dialog,
    DialogOverlay,
    DialogTitle,
    ErrorAlert,
    InfoAlert,
    TransitionChild,
    TransitionRoot,
  },
  data() {
    return {
      newpassword: "",
      confirmPassword: "",
      info: null,
      error: null,
      containsEightCharacters: false,
      containsNumber: false,
      containsUppercase: false,
      containsSpecialCharacter: false,
      match: false,
      show: false,
      validPassword: false,
    };
  },
  methods: {
    closeModal() {
      this.$emit("close");
      this.info = null;
      this.error = null;
      this.newpassword = "";
      this.confirmPassword = "";
    },
    closeInfo() {
      this.info = null;
    },
    closeError() {
      this.error = null;
    },
    cancel() {
      this.newpassword = "";
      this.error = null;
      this.info = null;
      this.confirmPassword = "";
      this.closeModal();
    },
    savePassword() {
      console.log("add password");
      addPassword(this.newpassword).then(() => {
        this.error = null;
        this.info = {message: "Password Added"};
        updateUser(this.$cookies.get("uid"), { needPassword: false });
      })
      .catch( (e) => {
        console.log("Add password error: " + e);
        e.message = "Error Adding Password";
        this.info = null;
        this.error = e;
      });
    },
    checkPassword() {
      let passwordLength = this.newpassword.length;
      const format = /[ !@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;
      if (passwordLength > 0) {
        this.show = true;
      } else {
        this.show = false;
      }

      if (passwordLength > 7) {
        this.containsEightCharacters = true;
      } else {
        this.containsEighCharacters = false;
      }

      if (this.newpassword == this.confirmPassword) {
        this.match = true;
      } else {
        this.match = false;
      }

      this.containsNumber = /\d/.test(this.newpassword);
      this.containsUppercase = /[A-Z]/.test(this.newpassword);
      this.containsSpecialCharacter = format.test(this.newpassword);

      if (
        this.containsEightCharacters === true &&
        this.containsSpecialCharacter === true &&
        this.containsUppercase === true &&
        this.containsNumber === true &&
        this.match === true
      ) {
        this.validPassword = true;
      } else {
        this.validPassword = false;
      }
    },
  },
};
</script>

